import React, { useState, useMemo, Component, ErrorInfo, ReactNode } from 'react';
import { Box, IconButton, Tooltip, Chip, Collapse, Alert, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '@mui/material/styles';
// @ts-ignore
import remarkGfm from 'remark-gfm';
// @ts-ignore
import remarkMath from 'remark-math';
// @ts-ignore
import rehypeKatex from 'rehype-katex';
// @ts-ignore
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';


interface MarkupMAIQProps {
  content: string;
}

// Error Boundary Component for Markdown rendering
class MarkdownErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Markdown Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'error.main', 
          borderRadius: 2,
          backgroundColor: 'error.light',
          color: 'error.dark',
          my: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Content Rendering Error
          </Typography>
          <Typography variant="body2">
            There was an error rendering the content. Please try refreshing or contact support.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// JSON Data Display Component
const JsonDataDisplay = ({ data, title }: { data: any; title?: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const isLarge = jsonString.length > 500;

  return (
    <Box sx={{ my: 2 }}>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'grey.50',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            backgroundColor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Chip
            label="JSON"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isLarge && (
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Collapse in={!isLarge || expanded}>
          <Box
            sx={{
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflowX: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <pre style={{ margin: 0 }}>{jsonString}</pre>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

// Enhanced Code Block Component
const CodeBlock = ({ inline, className, children, ...props }: any) => {
  const [copied, setCopied] = useState(false);
  const code = String(children).replace(/\n$/, '');
  const language = className?.replace('language-', '') || 'text';
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  if (inline) {
    return (
      <code
        style={{
          background: 'rgba(0,0,0,0.06)',
          borderRadius: '4px',
          padding: '2px 6px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
          color: 'inherit',
          fontWeight: 500,
        }}
      >
        {children}
      </code>
    );
  }

  return (
    <Box sx={{ position: 'relative', my: 2 }}>
      <Box
        sx={{
          background: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            backgroundColor: '#e9ecef',
            borderBottom: '1px solid #dee2e6',
          }}
        >
          <Chip
            label={language}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
          <Tooltip title={copied ? "Copied!" : "Copy code"}>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          sx={{
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflowX: 'auto',
            backgroundColor: '#ffffff',
            '& pre': {
              margin: 0,
              whiteSpace: 'pre',
            },
          }}
        >
          <pre>
            <code className={className} {...props}>{code}</code>
          </pre>
        </Box>
      </Box>
    </Box>
  );
};

// Table Component with enhanced styling and error handling
const TableComponent = ({ children, ...props }: any) => {
  try {
    return (
      <Box sx={{ overflowX: 'auto', my: 2, width: '100%' }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            minWidth: '100%',
            fontSize: '0.875rem',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            backgroundColor: 'background.paper',
          }}
          {...props}
        >
          {children}
        </Box>
      </Box>
    );
  } catch (error) {
    console.error('Table rendering error:', error);
    return (
      <Box sx={{ 
        p: 2, 
        border: '1px solid', 
        borderColor: 'warning.main', 
        borderRadius: 2,
        backgroundColor: 'warning.light',
        color: 'warning.dark',
        my: 2
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Table Rendering Error
        </Typography>
      </Box>
    );
  }
};

// Table Header Component
const TableHeader = ({ children, ...props }: any) => {
  return (
    <Box
      component="thead"
      sx={{
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Table Body Component
const TableBody = ({ children, ...props }: any) => {
  return (
    <Box component="tbody" {...props}>
      {children}
    </Box>
  );
};

// Table Row Component with error handling
const TableRow = ({ children, ...props }: any) => {
  try {
    return (
      <Box
        component="tr"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&:nth-of-type(even)': {
            backgroundColor: 'grey.50',
          },
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
        {...props}
      >
        {children}
      </Box>
    );
  } catch (error) {
    console.error('Table row rendering error:', error);
    return null;
  }
};

// Table Cell Component with error handling
const TableCell = ({ children, isHeader = false, ...props }: any) => {
  try {
    return (
      <Box
        component={isHeader ? 'th' : 'td'}
        sx={{
          padding: '12px 16px',
          textAlign: 'left',
          verticalAlign: 'top',
          borderRight: '1px solid',
          borderColor: 'divider',
          fontWeight: isHeader ? 600 : 400,
          fontSize: '0.875rem',
          wordBreak: 'break-word',
          maxWidth: '300px',
          '&:last-child': {
            borderRight: 'none',
          },
        }}
        {...props}
      >
        {children || ''}
      </Box>
    );
  } catch (error) {
    console.error('Table cell rendering error:', error);
    return (
      <Box
        component={isHeader ? 'th' : 'td'}
        sx={{
          padding: '12px 16px',
          color: 'error.main',
          fontStyle: 'italic',
        }}
      >
        Error rendering cell
      </Box>
    );
  }
};

// Content Parser to detect and handle different data types
const parseContent = (content: string) => {
  if (!content || typeof content !== 'string') {
    return { type: 'markdown', data: '', raw: '' };
  }

  // Try to detect JSON data
  const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
  if (jsonMatch) {
    try {
      const jsonData = JSON.parse(jsonMatch[1]);
      return { type: 'json', data: jsonData, raw: content };
    } catch {
      // If JSON parsing fails, treat as regular markdown
    }
  }

  // Try to detect if content is pure JSON
  try {
    const jsonData = JSON.parse(content.trim());
    return { type: 'json', data: jsonData, raw: content };
  } catch {
    // Not JSON, treat as markdown
  }

  // Check for structured data patterns (improved table detection)
  const lines = content.split('\n');
  const tableLines = lines.filter(line => 
    line.trim().includes('|') && 
    line.trim().length > 0 && 
    !line.trim().startsWith('#') // Exclude markdown headers
  );
  
  // If we have multiple lines with pipes, it's likely a table
  if (tableLines.length >= 2) {
    // Validate table structure
    const hasValidTableStructure = tableLines.some(line => {
      const parts = line.split('|').filter(part => part.trim().length > 0);
      return parts.length >= 2; // At least 2 columns
    });
    
    if (hasValidTableStructure) {
      return { type: 'table', data: content, raw: content };
    }
  }

  return { type: 'markdown', data: content, raw: content };
};

const MarkupMAIQ: React.FC<MarkupMAIQProps> = ({ content }) => {
  const theme = useTheme();
  const parsedContent = useMemo(() => parseContent(content), [content]);

  // Handle JSON data specially
  if (parsedContent.type === 'json') {
    return (
      <Box sx={{ width: '100%' }}>
        <JsonDataDisplay data={parsedContent.data} />
      </Box>
    );
  }

  // Error boundary for markdown rendering
  const renderMarkdown = () => {
    try {
      // Safe content cleaning without breaking table structure
      let cleanContent = content || '';
      
      // Only normalize line endings if content exists
      if (cleanContent && typeof cleanContent === 'string') {
        // Preserve table structure - don't normalize line endings for tables
        const hasTableStructure = cleanContent.includes('|') && 
          cleanContent.split('\n').filter(line => line.trim().includes('|')).length >= 2;
        
        if (!hasTableStructure) {
          // Only normalize line endings for non-table content
          cleanContent = cleanContent
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');
        }
        
        // Handle asterisks properly:
        // 1. Protect escaped asterisks (\*) by converting to placeholder
        // 2. Preserve valid markdown patterns (**text** for bold, *text* for italic)
        // 3. Remove unpaired asterisks (single or double asterisks not part of markdown)
        // 4. Restore escaped asterisks
        
        const ESCAPED_ASTERISK_PLACEHOLDER = '__ESCAPED_ASTERISK_PLACEHOLDER__';
        const TRIPLE_START = '__TRIPLE_START__';
        const TRIPLE_END = '__TRIPLE_END__';
        const DOUBLE_START = '__DOUBLE_START__';
        const DOUBLE_END = '__DOUBLE_END__';
        const SINGLE_START = '__SINGLE_START__';
        const SINGLE_END = '__SINGLE_END__';
        
        // Step 1: Protect escaped asterisks first
        cleanContent = cleanContent.replace(/\\\*/g, ESCAPED_ASTERISK_PLACEHOLDER);
        
        // Step 2: Protect valid markdown patterns (order matters - most specific first)
        // First, protect triple asterisks (***text***) for bold+italic
        cleanContent = cleanContent.replace(/\*\*\*([^*]+?)\*\*\*/g, (match, text) => {
          return `${TRIPLE_START}${text}${TRIPLE_END}`;
        });
        
        // Then protect double asterisks (**text**) for bold
        // Use a function that finds matching pairs, allowing single asterisks in content
        const protectDoubleAsterisks = (text: string): string => {
          const result: string[] = [];
          let i = 0;
          
          while (i < text.length) {
            // Look for ** pattern (triple patterns are already protected, so we won't see ***)
            if (i + 1 < text.length && text[i] === '*' && text[i + 1] === '*') {
              // Find the next ** that closes this (greedy match)
              let j = i + 2;
              let found = false;
              while (j < text.length - 1) {
                if (text[j] === '*' && text[j + 1] === '*') {
                  const content = text.substring(i + 2, j);
                  // Only if there's actual content (not just whitespace)
                  if (content.trim().length > 0) {
                    result.push(`${DOUBLE_START}${content}${DOUBLE_END}`);
                    i = j + 2;
                    found = true;
                    break;
                  }
                }
                j++;
              }
              if (!found) {
                // No closing found, skip these asterisks (they'll be removed later)
                i += 2;
              }
            } else {
              result.push(text[i]);
              i++;
            }
          }
          
          return result.join('');
        };
        
        cleanContent = protectDoubleAsterisks(cleanContent);
        
        // Protect single asterisks for italic (*text*) - only if properly paired
        // Must not be part of double or triple patterns
        cleanContent = cleanContent.replace(/(?<!\*)\*([^*\s\n][^*\n]*?[^*\s\n])\*(?!\*)/g, (match, text) => {
          // Check if this is not already part of a protected pattern
          if (!match.includes(DOUBLE_START) && 
              !match.includes(DOUBLE_END) &&
              !match.includes(TRIPLE_START) && 
              !match.includes(TRIPLE_END)) {
            return `${SINGLE_START}${text}${SINGLE_END}`;
          }
          return match;
        });
        
        // Handle single word/character italic: *word* or *a*
        cleanContent = cleanContent.replace(/(?<!\*)\*([^*\s\n]+)\*(?!\*)/g, (match, text) => {
          if (!match.includes(SINGLE_START) && 
              !match.includes(DOUBLE_START) && 
              !match.includes(TRIPLE_START)) {
            return `${SINGLE_START}${text}${SINGLE_END}`;
          }
          return match;
        });
        
        // Step 3: Remove all remaining unpaired asterisks (both single * and double **)
        cleanContent = cleanContent.replace(/\*+/g, '');
        
        // Step 4: Restore valid markdown patterns
        cleanContent = cleanContent.replace(new RegExp(TRIPLE_START, 'g'), '***');
        cleanContent = cleanContent.replace(new RegExp(TRIPLE_END, 'g'), '***');
        cleanContent = cleanContent.replace(new RegExp(DOUBLE_START, 'g'), '**');
        cleanContent = cleanContent.replace(new RegExp(DOUBLE_END, 'g'), '**');
        cleanContent = cleanContent.replace(new RegExp(SINGLE_START, 'g'), '*');
        cleanContent = cleanContent.replace(new RegExp(SINGLE_END, 'g'), '*');
        
        // Step 5: Restore escaped asterisks (these will show as literal asterisks)
        cleanContent = cleanContent.replace(new RegExp(ESCAPED_ASTERISK_PLACEHOLDER, 'g'), '*');
        
        cleanContent = cleanContent.trim();
      } else {
        cleanContent = '';
      }

      // If content is empty, return empty box
      if (!cleanContent) {
        return <Box sx={{ p: 1 }} />;
      }

      // Additional validation for problematic table content
      const isValidTableContent = (content: string) => {
        if (!content.includes('|')) return true;
        
        const lines = content.split('\n');
        const tableLines = lines.filter(line => line.trim().includes('|'));
        
        if (tableLines.length < 2) return true;
        
        // Check for malformed table structures
        for (const line of tableLines) {
          const parts = line.split('|').filter(part => part.trim().length > 0);
          if (parts.length === 0 || parts.length > 20) { // Too many columns might cause issues
            return false;
          }
        }
        
        return true;
      };

      // If table content is potentially problematic, use safer rendering
      if (!isValidTableContent(cleanContent)) {
        return (
          <Box sx={{ 
            p: 2, 
            border: '1px solid', 
            borderColor: 'warning.main', 
            borderRadius: 2,
            backgroundColor: 'warning.light',
            color: 'warning.dark'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Complex Table Content
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {cleanContent}
            </Typography>
          </Box>
        );
      }

      // Check if content has currency symbols to decide whether to use remarkMath
      const hasCurrency = /\$\d/.test(cleanContent);
      const plugins: any[] = [
        [remarkGfm, { 
          singleTilde: false, 
          tablePipeAlign: false,
          tableCellPadding: true,
          tableCellLineStyle: 'single'
        }]
      ];
      
      // Only use remarkMath if content doesn't have currency symbols
      // to avoid conflicts with dollar signs in prices
      if (!hasCurrency) {
        plugins.push(remarkMath);
      }

      // Try with full plugin support first
      try {
        return (
          <Box sx={{ 
            '& .react-markdown': {
              width: '100%',
              color: 'text.primary',
              '& p': {
                color: 'text.primary',
                margin: '0.5em 0',
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'text.primary',
                margin: '0.5em 0',
              },
              '& strong, & b': {
                color: 'text.primary',
                fontWeight: 600,
              },
              '& em, & i': {
                color: 'text.primary',
                fontStyle: 'italic',
              },
              '& ul, & ol': {
                color: 'text.primary',
                margin: '0.5em 0',
                paddingLeft: '1.5em',
              },
              '& li': {
                color: 'text.primary',
                margin: '0.25em 0',
              },
              '& blockquote': {
                color: 'text.secondary',
                borderLeft: '4px solid',
                borderLeftColor: 'divider',
                paddingLeft: '1em',
                margin: '1em 0',
                fontStyle: 'italic',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }
          }}>
            <ReactMarkdown
              remarkPlugins={plugins}
              rehypePlugins={[]}
              components={{
                code: CodeBlock,
                table: TableComponent,
                thead: TableHeader,
                tbody: TableBody,
                tr: TableRow,
                th: (props) => <TableCell {...props} isHeader={true} />,
                td: TableCell,
              }}
              skipHtml={false}
              transformLinkUri={undefined}
              transformImageUri={undefined}
            >
              {cleanContent}
            </ReactMarkdown>
          </Box>
        );
      } catch (gfmError) {
        console.warn('GFM plugin failed, falling back to basic markdown:', gfmError);
        
        // Fallback to basic markdown without GFM table support
        // Don't use remarkMath in fallback if content has currency
        const fallbackPlugins = hasCurrency ? [] : [remarkMath];
        
        return (
          <Box sx={{ 
            '& .react-markdown': {
              width: '100%',
              color: 'text.primary',
              '& p': {
                color: 'text.primary',
                margin: '0.5em 0',
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'text.primary',
                margin: '0.5em 0',
              },
              '& strong, & b': {
                color: 'text.primary',
                fontWeight: 600,
              },
              '& em, & i': {
                color: 'text.primary',
                fontStyle: 'italic',
              },
              '& ul, & ol': {
                color: 'text.primary',
                margin: '0.5em 0',
                paddingLeft: '1.5em',
              },
              '& li': {
                color: 'text.primary',
                margin: '0.25em 0',
              },
              '& blockquote': {
                color: 'text.secondary',
                borderLeft: '4px solid',
                borderLeftColor: 'divider',
                paddingLeft: '1em',
                margin: '1em 0',
                fontStyle: 'italic',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }
          }}>
            <ReactMarkdown
              remarkPlugins={fallbackPlugins}
              rehypePlugins={[]}
              components={{
                code: CodeBlock,
                // Remove table components for fallback
              }}
              skipHtml={false}
              transformLinkUri={undefined}
              transformImageUri={undefined}
            >
              {cleanContent}
            </ReactMarkdown>
          </Box>
        );
      }
    } catch (error) {
      console.error('Markdown rendering error:', error);
      // Fallback to plain text if markdown fails
      return (
        <Box sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'error.main', 
          borderRadius: 2,
          backgroundColor: 'error.light',
          color: 'error.dark'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Content Rendering Error
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        fontFamily: theme.typography.fontFamily,
        whiteSpace: 'normal',
        '& *': {
          fontFamily: `${theme.typography.fontFamily} !important`,
        },
        // Reduce excessive spacing between markdown elements
        '& > * + *': {
          marginTop: '8px !important',
        },
        '& > ul + *': {
          marginTop: '12px !important',
        },
        '& > * + ul': {
          marginTop: '12px !important',
        },
        '& > table + *': {
          marginTop: '12px !important',
        },
        '& > * + table': {
          marginTop: '12px !important',
        },
        // Enhanced table styling
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          margin: '12px 0',
          fontSize: '0.875rem',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        '& thead': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        '& th, & td': {
          border: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': {
          fontWeight: 600,
          fontSize: '0.875rem',
        },
        '& tr:nth-of-type(even)': {
          backgroundColor: theme.palette.grey[50],
        },
        '& tr:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        // Enhanced list styling
        '& ul, & ol': {
          margin: '8px 0',
          paddingLeft: '16px',
          lineHeight: 1.2,
        },
        '& li': {
          margin: '4px 0',
          lineHeight: 1.1,
          padding: '0px',
        },
        '& ul > li': {
          listStyleType: 'disc',
        },
        '& ol > li': {
          listStyleType: 'decimal',
        },
        // Nested lists with better spacing
        '& ul ul, & ol ol, & ul ol, & ol ul': {
          margin: '0px',
          paddingLeft: '12px',
        },
        '& ul ul > li': {
          listStyleType: 'circle',
        },
        '& ul ul ul > li': {
          listStyleType: 'square',
        },
        '& ol ol > li': {
          listStyleType: 'lower-alpha',
        },
        '& ol ol ol > li': {
          listStyleType: 'lower-roman',
        },
        // Enhanced blockquote styling
        '& blockquote': {
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          margin: '0px',
          padding: '8px 12px',
          fontStyle: 'italic',
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.grey[50],
          borderRadius: '0 8px 8px 0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '& p': {
            margin: '0px',
            '&:last-child': {
              margin: 0,
            },
          },
        },
        // Enhanced heading styling
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '16px 0 8px 0',
          fontWeight: 600,
          lineHeight: 1.2,
          color: theme.palette.text.primary,
          '&:first-child': {
            marginTop: 0,
          },
        },
        '& h1': { 
          fontSize: '1.75em',
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          paddingBottom: '8px',
        },
        '& h2': { 
          fontSize: '1.5em',
          borderBottom: `1px solid ${theme.palette.divider}`,
          paddingBottom: '6px',
        },
        '& h3': { fontSize: '1.25em' },
        '& h4': { fontSize: '1.1em' },
        '& h5': { fontSize: '1em' },
        '& h6': { fontSize: '0.9em' },
        // Enhanced paragraph styling
        '& p': {
          margin: '8px 0',
          lineHeight: 1.4,
          '&:first-child': {
            marginTop: 0,
          },
          '&:last-child': {
            marginBottom: 0,
          },
        },
        // Enhanced horizontal rule
        '& hr': {
          border: 'none',
          borderTop: `2px solid ${theme.palette.divider}`,
          margin: '32px 0',
          borderRadius: '1px',
        },
        // Link styling
        '& a': {
          color: theme.palette.primary.main,
          textDecoration: 'none',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline',
            color: theme.palette.primary.dark,
          },
        },
        // Strong and emphasis styling
        '& strong, & b': {
          fontWeight: 600,
          color: theme.palette.text.primary,
        },
        '& em, & i': {
          fontStyle: 'italic',
          color: theme.palette.text.secondary,
        },
        // Image styling
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          margin: '16px 0',
        },
        // Alert/Info box styling
        '& .alert, & .info, & .warning, & .error': {
          padding: '16px',
          margin: '16px 0',
          borderRadius: '8px',
          border: '1px solid',
          '&.alert-info, &.info': {
            backgroundColor: theme.palette.info.light,
            borderColor: theme.palette.info.main,
            color: theme.palette.info.dark,
          },
          '&.alert-warning, &.warning': {
            backgroundColor: theme.palette.warning.light,
            borderColor: theme.palette.warning.main,
            color: theme.palette.warning.dark,
          },
          '&.alert-error, &.error': {
            backgroundColor: theme.palette.error.light,
            borderColor: theme.palette.error.main,
            color: theme.palette.error.dark,
          },
        },
      }}
    >
      <MarkdownErrorBoundary>
        {renderMarkdown()}
      </MarkdownErrorBoundary>
    </Box>
  );
};

export default MarkupMAIQ; 